import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ActivitySelect from "../components/ActivitySelect";

const HERO = "https://images.unsplash.com/photo-1603521097795-3b798129197f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxtaWRkbGUlMjBlYXN0JTIwYnVzaW5lc3MlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lsaW5lfGVufDB8fHx8MTc4NzgwNzMzNnww&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    email: "", password: "", name: "", legal_name: "", address: "", website: "",
    contact_person_name: "", contact_person_role: "", contact_phone: "", industry: "", activity_code: "", division_code: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(f);
      toast.success(t("register.success"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (id, label, phKey, opts = {}) => (
    <div>
      <Label htmlFor={id} className="text-midnight text-sm">{label}</Label>
      <Input id={id} value={f[id]} onChange={set(id)} required={opts.required} type={opts.type || "text"}
        placeholder={t(`register.ph.${phKey}`)} data-testid={`register-${id}-input`}
        className="mt-1.5 rounded-none border-sand focus-visible:ring-gold" />
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-bone" data-testid="register-page">
      <div className="lg:col-span-3 flex items-center justify-center p-8 sm:p-12 lg:p-16 overflow-y-auto relative">
        <div className="absolute top-4 end-4"><LanguageSwitcher /></div>
        <div className="w-full max-w-2xl animate-fade-up">
          <Link to="/" className="font-serif text-2xl font-bold text-midnight tracking-tight">Qafila</Link>
          <p className="overline text-gold mt-8">{t("register.overline")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-midnight mt-2 tracking-tight">{t("register.title")}</h1>
          <p className="text-sm text-muted-foreground mt-2">{t("register.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-6">
            <div>
              <p className="overline text-muted-foreground mb-3">{t("register.account")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {field("name", t("register.name"), "name", { required: true })}
                {field("email", t("common.email"), "email", { required: true, type: "email" })}
                {field("password", t("common.password"), "password", { required: true, type: "password" })}
              </div>
            </div>
            <div>
              <p className="overline text-muted-foreground mb-3">{t("register.company")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {field("legal_name", t("register.legalName"), "legalName", { required: true })}
                {field("industry", t("register.industry"), "industry")}
                {field("address", t("register.address"), "address")}
                {field("website", t("register.website"), "website")}
              </div>
              <div className="mt-4">
                <ActivitySelect value={f.activity_code} onChange={(v) => setF({ ...f, activity_code: v, division_code: "" })}
                  divisionValue={f.division_code} onDivisionChange={(v) => setF({ ...f, division_code: v })}
                  testId="register-activity-select" divisionTestId="register-division-select" />
              </div>
            </div>
            <div>
              <p className="overline text-muted-foreground mb-3">{t("register.referent")}</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {field("contact_person_name", t("register.refName"), "refName")}
                {field("contact_person_role", t("register.role"), "role")}
                {field("contact_phone", t("register.phone"), "phone")}
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="w-full h-12 rounded-none bg-gold text-midnight hover:brightness-105 transition-all duration-300 font-medium">
              {loading ? t("register.submitting") : t("register.submit")}
            </Button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-sand" />
            <span className="text-xs text-muted-foreground">{t("common.orDivider")}</span>
            <div className="h-px flex-1 bg-sand" />
          </div>
          <Button onClick={googleLogin} variant="outline" data-testid="register-google-btn"
            className="w-full h-12 rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone transition-all duration-300">
            {t("common.continueWithGoogle")}
          </Button>

          <p className="mt-6 text-sm text-muted-foreground">
            {t("register.haveAccount")}{" "}
            <Link to="/login" className="text-midnight font-medium underline decoration-gold underline-offset-4" data-testid="to-login-link">{t("register.loginLink")}</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block lg:col-span-2 relative">
        <img src={HERO} alt="Middle East skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-midnight/45" />
        <div className="absolute bottom-16 start-10 end-10 text-bone">
          <p className="overline text-gold mb-3">Qafila</p>
          <p className="font-serif text-2xl leading-tight">{t("register.heroText")}</p>
        </div>
      </div>
    </div>
  );
}
